import SEO from '../SEO';
import { Monitor, Smartphone, Zap, Palette, Code, CheckCircle, ArrowRight, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import DomainChecker from '../DomainChecker';
import { Link } from 'react-router-dom';

function WebDesignArlington() {
  const features = [
    { icon: <Monitor className="h-8 w-8" />, title: "Custom Design", desc: "Unique designs tailored to your Arlington business brand" },
    { icon: <Smartphone className="h-8 w-8" />, title: "Mobile-First", desc: "Responsive websites that work perfectly on all devices" },
    { icon: <Zap className="h-8 w-8" />, title: "Lightning Fast", desc: "Optimized for speed with sub-2-second load times" },
    { icon: <Palette className="h-8 w-8" />, title: "Modern UI/UX", desc: "Beautiful interfaces that convert visitors to customers" },
    { icon: <Code className="h-8 w-8" />, title: "Clean Code", desc: "SEO-friendly, maintainable, and scalable development" },
    { icon: <CheckCircle className="h-8 w-8" />, title: "ADA Compliant", desc: "Accessible websites that meet WCAG guidelines" }
  ];

  const packages = [
    { name: "Starter", price: "$1,499", features: ["5 Pages", "Mobile Responsive", "Contact Form", "Basic SEO", "1 Month Support"] },
    { name: "Business", price: "$3,499", features: ["10 Pages", "Custom Design", "Blog Setup", "Advanced SEO", "3 Months Support"], popular: true },
    { name: "Enterprise", price: "$7,999+", features: ["Unlimited Pages", "eCommerce Ready", "Custom Features", "Full SEO Suite", "12 Months Support"] }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Web Design Arlington TX | Custom Website Development | Web-TechServices"
        description="Professional web design services in Arlington, Texas. Custom, mobile-responsive websites for Arlington businesses. Get a free quote today!"
        keywords="web design Arlington TX, Arlington web designer, custom website Arlington, responsive web design Arlington Texas, website development Arlington, Arlington website company"
        canonical="https://web-techservices.com/services/web-design-arlington"
      />
      
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <span className="bg-blue-500/30 text-blue-200 px-4 py-1 rounded-full text-sm font-medium">Arlington's #1 Web Design Agency</span>
          <h1 className="text-4xl md:text-6xl font-bold mt-6 mb-6">
            Professional Web Design<br />in Arlington, Texas
          </h1>
          <p className="text-xl text-blue-200 mb-8 max-w-2xl">
            Beautiful, high-converting websites built specifically for Arlington businesses. Stand out from competitors with custom design that captures your brand.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/book-consultation">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 font-bold px-8">
                Get Free Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/ai-search">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-bold px-8">
                Analyze Your Current Site
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="text-3xl font-bold text-center mb-4">What Makes Our Arlington Web Design Different</h2>
        <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">We don't just build websites - we create digital experiences that drive results for Arlington businesses</p>
        <div className="grid md:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <div key={i} className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4 text-blue-600">
                {f.icon}
              </div>
              <h3 className="font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-slate-600">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-slate-50 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Arlington Web Design Packages</h2>
          <p className="text-center text-slate-600 mb-12">Transparent pricing for Arlington businesses of all sizes</p>
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, i) => (
              <Card key={i} className={`relative ${pkg.popular ? 'border-2 border-blue-500 shadow-xl' : ''}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-blue-600 mb-6">{pkg.price}</div>
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-blue-500" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/book-consultation">
                    <Button className={`w-full ${pkg.popular ? 'bg-blue-600 hover:bg-blue-700' : ''}`} variant={pkg.popular ? 'default' : 'outline'}>
                      Get Started
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-600 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready for a New Website?</h2>
          <p className="text-xl text-blue-100 mb-8">Let's discuss your Arlington web design project. Free consultation, no obligations.</p>
          <Link to="/book-consultation">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 font-bold">
              Schedule Free Consultation
            </Button>
          </Link>
        </div>
      </section>

      {/* Domain Checker */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-4 max-w-2xl">
          <DomainChecker headline="Claim Your Arlington Business Domain" subtitle="Get a custom domain with your new website — starting at $15.99/year" compact={true} />
        </div>
      </section>

    </div>
  );
}

export default WebDesignArlington;
